/**
 * ============================================================================
 * SESSION TRACKER
 * ============================================================================
 * 
 * Tracks total time spent on VowAI using heartbeat pattern.
 * One instance in App.js covers all pages.
 * 
 * - Heartbeat every 30s with cumulative visible time
 * - Per-page time tracking (resets on route change)
 * - Pauses when tab hidden, resumes when visible
 * - Session ID shared across tabs via localStorage
 * - New session if last heartbeat > 5 min ago
 * - Exponential backoff on failure (30s → 60s → 120s → 240s → 300s cap)
 * - Circuit breaker: stops after 3 consecutive failures, probes after 5 min
 * 
 * ============================================================================
 */

import { API_BASE_URL } from '../config/api';

const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const MAX_INTERVAL = 300000; // 5 min cap
const SESSION_TIMEOUT = 5 * 60 * 1000; // 5 minutes
const MAX_FAILURES = 3; // circuit breaker threshold

function generateId() {
  return 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

class SessionTracker {
  constructor() {
    this.sessionId = this._getOrCreateSession();
    this.userId = null;
    this.currentPage = this._normalizePath(window.location.pathname);
    this.pageStartTime = Date.now();
    this.sessionStartTime = Date.now();
    this.totalVisibleTime = 0;
    this.pageVisibleTime = 0;
    this.isVisible = !document.hidden;
    this.lastVisibleTimestamp = this.isVisible ? Date.now() : null;

    // Backoff & circuit breaker state
    this.consecutiveFailures = 0;
    this.currentInterval = HEARTBEAT_INTERVAL;
    this.circuitOpen = false;

    this._boundVisibility = this._onVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._boundVisibility);

    this._scheduleHeartbeat();
  }

  _scheduleHeartbeat() {
    clearTimeout(this.heartbeatTimer);
    this.heartbeatTimer = setTimeout(() => {
      this._sendHeartbeat();
      this._scheduleHeartbeat();
    }, this.currentInterval);
  }

  _onSuccess() {
    this.consecutiveFailures = 0;
    this.circuitOpen = false;
    if (this.currentInterval !== HEARTBEAT_INTERVAL) {
      this.currentInterval = HEARTBEAT_INTERVAL;
      this._scheduleHeartbeat(); // reset to normal cadence
    }
  }

  _onFailure() {
    this.consecutiveFailures++;
    if (this.consecutiveFailures >= MAX_FAILURES) {
      // Circuit breaker: stop heartbeats, probe after MAX_INTERVAL
      this.circuitOpen = true;
      this.currentInterval = MAX_INTERVAL;
    } else {
      // Exponential backoff: double the interval, cap at MAX_INTERVAL
      this.currentInterval = Math.min(this.currentInterval * 2, MAX_INTERVAL);
    }
    this._scheduleHeartbeat();
  }

  _getOrCreateSession() {
    const existing = localStorage.getItem('vowai_session_id');
    const lastBeat = parseInt(localStorage.getItem('vowai_last_heartbeat') || '0', 10);
    const now = Date.now();

    if (existing && (now - lastBeat) < SESSION_TIMEOUT) {
      return existing;
    }

    const newId = generateId();
    localStorage.setItem('vowai_session_id', newId);
    localStorage.setItem('vowai_last_heartbeat', String(now));
    return newId;
  }

  _onVisibilityChange() {
    const now = Date.now();

    if (document.hidden) {
      if (this.lastVisibleTimestamp) {
        const elapsed = now - this.lastVisibleTimestamp;
        this.totalVisibleTime += elapsed;
        this.pageVisibleTime += elapsed;
      }
      this.isVisible = false;
      this.lastVisibleTimestamp = null;
    } else {
      this.isVisible = true;
      this.lastVisibleTimestamp = now;
    }
  }

  _getCurrentTimes() {
    let totalExtra = 0;
    let pageExtra = 0;

    if (this.isVisible && this.lastVisibleTimestamp) {
      const elapsed = Date.now() - this.lastVisibleTimestamp;
      totalExtra = elapsed;
      pageExtra = elapsed;
    }

    return {
      totalSeconds: Math.round((this.totalVisibleTime + totalExtra) / 1000),
      pageSeconds: Math.round((this.pageVisibleTime + pageExtra) / 1000)
    };
  }

  _sendHeartbeat() {
    if (!this.isVisible) return;

    const { totalSeconds, pageSeconds } = this._getCurrentTimes();

    const data = {
      session_id: this.sessionId,
      user_id: this.userId,
      page: this.currentPage,
      page_time_seconds: pageSeconds,
      total_session_time_seconds: totalSeconds
    };

    localStorage.setItem('vowai_last_heartbeat', String(Date.now()));

    const url = `${API_BASE_URL}/feedback/session-heartbeat`;

    try {
      fetch(url, {
        method: 'POST',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'text/plain' },
        keepalive: true // survives page unload like sendBeacon
      }).then(res => {
        if (res.ok) this._onSuccess();
        else this._onFailure();
      }).catch(() => this._onFailure());
    } catch {
      this._onFailure();
    }
  }

  setUser(userId) {
    this.userId = userId;
  }

  onRouteChange(newPath) {
    if (newPath === this.currentPage) return;

    if (this.isVisible && this.lastVisibleTimestamp) {
      const elapsed = Date.now() - this.lastVisibleTimestamp;
      this.totalVisibleTime += elapsed;
      this.pageVisibleTime += elapsed;
      this.lastVisibleTimestamp = Date.now();
    }

    this._sendHeartbeat();

    this.currentPage = this._normalizePath(newPath);
    this.pageVisibleTime = 0;
  }

  _normalizePath(path) {
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }

  destroy() {
    clearTimeout(this.heartbeatTimer);
    document.removeEventListener('visibilitychange', this._boundVisibility);
  }
}

export default SessionTracker;
